import { showConfirm, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { ref, type Ref } from 'vue';

interface TrackedItem {
  id: number;
  tracking_no?: string | null;
  tracking_link?: string | null;
}

interface OrderLike {
  ecommerce_order_id?: unknown;
}

/**
 * AB#20739 — remove an incorrect or duplicate tracking entry from an order item.
 *
 * The backend clears the carrier, number, link and MAWB and drops the carrier scans
 * fetched for them. The item stays Shipped and nobody is e-mailed. A fulfilment
 * already sent to a webshop is not undone, so the confirmation says so on those orders.
 */
export function useDeleteTrackingDetails(): {
  isDeletingTracking: Ref<boolean>;
  deleteTrackingDetails: (
    item: TrackedItem | null | undefined,
    order?: OrderLike | null
  ) => Promise<boolean>;
} {
  const isDeletingTracking = ref(false);

  /** Resolves true when the entry was deleted. */
  const deleteTrackingDetails = async (
    item: TrackedItem | null | undefined,
    order?: OrderLike | null
  ): Promise<boolean> => {
    if (!item || isDeletingTracking.value) return false;

    const trackingNo = item.tracking_no || item.tracking_link || '';
    const webshopId = order?.ecommerce_order_id;
    const fromWebshop =
      webshopId !== null && webshopId !== undefined && String(webshopId).trim() !== '';
    const confirmed = await showConfirm(
      'Delete tracking details?',
      `Removes the carrier, tracking number ${trackingNo}, link and MAWB from this item, ` +
        'with its tracking history. The order stays Shipped.' +
        (fromWebshop
          ? ' This order came from a webshop: a fulfilment already sent to the shop is not undone.'
          : '')
    );
    if (!confirmed) return false;

    isDeletingTracking.value = true;
    try {
      const { data } = await http.post('/delete-shipping-link', { order_item_id: item.id });
      if (data?.success === false) {
        showToast(data.message || 'Could not delete the tracking details.', 'error');
        return false;
      }
      showToast('Tracking details deleted');
      return true;
    } catch (error) {
      handleResponseException(error);
      return false;
    } finally {
      isDeletingTracking.value = false;
    }
  };

  return { isDeletingTracking, deleteTrackingDetails };
}
